import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { collection, query, where, getDocs, onSnapshot, writeBatch, serverTimestamp, doc, getDoc, addDoc, updateDoc, deleteDoc, orderBy, Timestamp, collectionGroup, deleteField } from 'firebase/firestore';
import { db } from '../../../utils/firebase';
import { School, Teacher, User, isTeacher, isSuperAdmin } from '../../../utils/firebase';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '../../../utils/useMediaQuery';
import Card from '../../../components/ui/Card';
import ToggleSwitch from '../../../components/ui/ToggleSwitch';
import ScheduleSettingDialog from '../../../components/ui/ScheduleSettingDialog';
import LeftArrowIcon from '../../../components/icons/LeftArrowIcon';
import RightArrowIcon from '../../../components/icons/RightArrowIcon';
import LeftArrowDoubleIcon from '../../../components/icons/LeftArrowDoubleIcon';
import RightArrowDoubleIcon from '../../../components/icons/RightArrowDoubleIcon';
import SettingIcon from '../../../components/icons/SettingIcon';
import TrashIcon from '../../../components/icons/TrashIcon';
import PlusIcon from '../../../components/icons/PlusIcon';
import { Schedule } from '../../../types/schedule';

const ScheduleManagement: React.FC = () => {
    const { currentUser, setCurrentUser } = useAuth();
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    
    // 일정의 시작 날짜를 가져오는 헬퍼 함수
    const getScheduleStartDate = (schedule: Schedule): Date => {
        if (schedule.dateOption === 'allDay' && schedule.date) {
            return schedule.date.toDate();
        }
        if (schedule.dateOption === 'custom' && schedule.startDate) {
            return schedule.startDate.toDate();
        }
        // period 옵션이나 날짜가 없는 경우 (나중에 정렬)
        return new Date(9999, 11, 31);
    };

    // 일정 날짜 비교 헬퍼 함수
    const isScheduleOnDate = (schedule: Schedule, date: number, year?: number, month?: number): boolean => {
        // 년/월이 제공되지 않으면 currentDate 사용
        const checkYear = year ?? currentDate.getFullYear();
        const checkMonth = month ?? currentDate.getMonth();
        
        // 날짜만 비교하기 위한 헬퍼 함수 (시간 정보 제거)
        const normalizeDate = (d: Date) => {
            return new Date(d.getFullYear(), d.getMonth(), d.getDate());
        };
        
        // allDay 옵션
        if (schedule.dateOption === 'allDay' && schedule.date) {
            const scheduleDate = schedule.date.toDate();
            return scheduleDate.getDate() === date && 
                   scheduleDate.getMonth() === checkMonth && 
                   scheduleDate.getFullYear() === checkYear;
        }
        
        // custom 옵션
        if (schedule.dateOption === 'custom' && schedule.startDate && schedule.endDate) {
            const startDate = normalizeDate(schedule.startDate.toDate());
            const endDate = normalizeDate(schedule.endDate.toDate());
            const checkDate = new Date(checkYear, checkMonth, date);
            // 날짜 범위 비교 (시작일과 종료일 모두 포함)
            // normalizeDate로 정규화된 날짜들끼리 비교
            const normalizedCheckDate = normalizeDate(checkDate);
            return normalizedCheckDate >= startDate && normalizedCheckDate <= endDate;
        }
        
        // period 옵션 - 날짜 직접 비교는 어려우므로 false 반환
        return false;
    };
    
    // 달력 생성 함수
    const generateCalendar = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        
        // 현재 월의 첫 번째 날과 마지막 날
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        
        // 첫 번째 날의 요일 (0=일요일, 1=월요일, ...)
        const firstDayOfWeek = firstDay.getDay();
        
        // 현재 월의 일수
        const daysInMonth = lastDay.getDate();
        
        // 이전 달의 마지막 날들 (빈 칸 채우기용)
        const prevMonth = new Date(year, month, 0);
        const daysInPrevMonth = prevMonth.getDate();
        
        const calendar = [];
        
        // 이전 달의 날짜들 (빈 칸 채우기)
        for (let i = firstDayOfWeek - 1; i >= 0; i--) {
        calendar.push({
            date: daysInPrevMonth - i,
            isCurrentMonth: false,
            isToday: false,
            isSelected: false
        });
        }
        
        // 현재 달의 날짜들
        const today = new Date();
        for (let day = 1; day <= daysInMonth; day++) {
        const isToday = day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
        const isSelected = day === selectedDate.getDate() && month === selectedDate.getMonth() && year === selectedDate.getFullYear();
        calendar.push({
            date: day,
            isCurrentMonth: true,
            isToday,
            isSelected
        });
        }
        
        // 다음 달의 날짜들 (빈 칸 채우기)
        const remainingCells = 42 - calendar.length; // 6주 * 7일 = 42칸
        for (let day = 1; day <= remainingCells; day++) {
        calendar.push({
            date: day,
            isCurrentMonth: false,
            isToday: false,
            isSelected: false
        });
        }
        
        return calendar;
    };
    const [schedules, setSchedules] = useState<Schedule[]>([]);
    const [currentScheduleType, setCurrentScheduleType] = useState<'class' | 'subject'>('class');
    
    // 현재 표시할 년월 상태 추가
    const [currentDate, setCurrentDate] = useState(new Date());
    // 선택된 날짜 상태 추가
    const [selectedDate, setSelectedDate] = useState(new Date());
    // 페이지네이션 상태 추가
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    
    // ScheduleSettingDialog 상태 추가
    const [isAddScheduleDialogOpen, setIsAddScheduleDialogOpen] = useState(false);
    const [selectedDateForSchedule, setSelectedDateForSchedule] = useState<Date | null>(null);
    const [editingSchedule, setEditingSchedule] = useState<any>(null);
    const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
    
    // 월 변경 함수들
    const goToPreviousMonth = () => {
        setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    };
    
    const goToNextMonth = () => {
        setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    };
    
    // 날짜 클릭 함수
    const handleDateClick = (day: number, isCurrentMonth: boolean) => {
        if (isCurrentMonth) {
            setSelectedDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), day));
        }
    };
    
    // 날짜 더블클릭 함수 (일정 추가)
    const handleDateDoubleClick = (day: number, isCurrentMonth: boolean) => {
        if (isCurrentMonth) {
            const clickedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
            setSelectedDateForSchedule(clickedDate);
            setDialogMode('create');
            setEditingSchedule(null);
            setIsAddScheduleDialogOpen(true);
        }
    };

    // 일정 수정 함수
    const handleEditSchedule = (schedule: any) => {
        setEditingSchedule(schedule);
        setDialogMode('edit');
        setSelectedDateForSchedule(null); // 수정 시에는 날짜 선택 안함
        setIsAddScheduleDialogOpen(true);
    };

    // 일정 삭제 함수
    const handleDeleteSchedule = async (schedule: Schedule) => {
        if (!currentUser || !isTeacher(currentUser)) return;
        
        const teacher = currentUser as Teacher;
        
        if (!window.confirm('정말로 이 일정을 삭제하시겠습니까?')) {
            return;
        }
        
        try {
            // 일정 타입에 따라 적절한 경로에서 삭제
            const scheduleType = schedule.type === 'subject' ? 'subject' : 'class';
            
            let docRef;
            if (scheduleType === 'class' && teacher.classId && schedule.id) {
                docRef = doc(
                    db,
                    `schools/${teacher.schoolId}/classes/${teacher.classId}/schedules/${schedule.id}`
                );
            } else if (scheduleType === 'subject' && teacher.subjectId && schedule.id) {
                docRef = doc(
                    db,
                    `schools/${teacher.schoolId}/subjects/${teacher.subjectId}/schedules/${schedule.id}`
                );
            }
            
            if (docRef) {
                await deleteDoc(docRef);
                setSuccess('일정이 삭제되었습니다.');
            } else {
                setError('일정을 삭제할 수 없습니다.');
            }
        } catch (err) {
            console.error('Error deleting schedule:', err);
            setError('일정 삭제 중 오류가 발생했습니다.');
        }
    };
    
    // 페이지네이션 함수들
    const totalPages = Math.ceil(schedules.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentSchedules = schedules.slice(startIndex, endIndex);
    
    const goToPage = (page: number) => {
        setCurrentPage(page);
    };
    
    const goToPreviousPage = () => {
        setCurrentPage(prev => Math.max(prev - 1, 1));
    };
    
    const goToNextPage = () => {
        setCurrentPage(prev => Math.min(prev + 1, totalPages));
    };
    
    const goToFirstPage = () => {
        setCurrentPage(1);
    };
    
    const goToLastPage = () => {
        setCurrentPage(totalPages);
    };

    // 일정 데이터 조회 (실시간 업데이트)
    useEffect(() => {
        if (!currentUser || !isTeacher(currentUser)) {
          setLoading(false);
          return;
        }
    
            setLoading(true);
        setError(null);
        
        const teacher = currentUser as Teacher;
        let unsubscribe: (() => void) | null = null;
        
        try {
            // 학급 일정 조회
            if (teacher.classId && currentScheduleType === 'class') {
              const schedulesRef = collection(
                db,
                `schools/${teacher.schoolId}/classes/${teacher.classId}/schedules`
              );
              
              const q = query(
                schedulesRef,
                where('type', '==', 'class'),
                orderBy('createdAt', 'desc')
              );
              
              unsubscribe = onSnapshot(
                q, 
                (snapshot) => {
                  const fetchedSchedules = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                  })) as Schedule[];
                  
                  // 날짜 빠른 순으로 정렬
                  const sortedSchedules = fetchedSchedules.sort((a, b) => {
                    const dateA = getScheduleStartDate(a).getTime();
                    const dateB = getScheduleStartDate(b).getTime();
                    return dateA - dateB;
                  });
                  
                  setSchedules(sortedSchedules);
                  setLoading(false);
                }, 
                (err) => {
                  setError('일정 데이터를 불러오는 중 오류가 발생했습니다.');
                  console.error('Error fetching schedules:', err);
                  setLoading(false);
                }
              );
            }
            
            // 교과 일정 조회
            if (teacher.subjectId && currentScheduleType === 'subject') {
              const schedulesRef = collection(
                db,
                `schools/${teacher.schoolId}/subjects/${teacher.subjectId}/schedules`
              );
              
              const q = query(
                schedulesRef,
                where('type', '==', 'subject'),
                orderBy('createdAt', 'desc')
              );
              
              unsubscribe = onSnapshot(
                q, 
                (snapshot) => {
                  const fetchedSchedules = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                  })) as Schedule[];
                  
                  // 날짜 빠른 순으로 정렬
                  const sortedSchedules = fetchedSchedules.sort((a, b) => {
                    const dateA = getScheduleStartDate(a).getTime();
                    const dateB = getScheduleStartDate(b).getTime();
                    return dateA - dateB;
                  });
                  
                  setSchedules(sortedSchedules);
                  setLoading(false);
                }, 
                (err) => {
                  setError('일정 데이터를 불러오는 중 오류가 발생했습니다.');
                  console.error('Error fetching schedules:', err);
                  setLoading(false);
                }
              );
            }
            
            // 일정 타입에 맞는 컬렉션이 없는 경우
            if (!(teacher.classId && currentScheduleType === 'class') && 
                !(teacher.subjectId && currentScheduleType === 'subject')) {
              setSchedules([]);
              setLoading(false);
            }
            
          } catch (err) {
            setError('일정 데이터를 불러오는 중 오류가 발생했습니다.');
            console.error('Error setting up schedule listener:', err);
            setLoading(false);
          }
    
        // cleanup function: 컴포넌트 언마운트 시 구독 해제
        return () => {
          if (unsubscribe) {
            unsubscribe();
          }
        };
      }, [currentUser?.uid, currentUser?.schoolId, currentUser?.classId, currentUser?.subjectId, currentScheduleType]);
    
  return (
    <div style={{ 
        width: '100%',
        height: isMobile ? '100%' : 'auto',
        fontFamily: 'Pretendard',
        overflow: isMobile ? 'hidden' : 'visible',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{
            width: '100%',
            height: isMobile ? '100%' : 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            gap: isMobile ? '16px' : '32px',
            flex: isMobile ? '1 1 0' : '0 0 auto',
            overflow: isMobile ? 'hidden' : 'visible'
        }}>
            <h1 style={{ 
                fontSize: isMobile ? '24px' : '32px',
                fontWeight: '600',
                lineHeight: isMobile ? '36px' : '48px',
                wordWrap: 'break-word',
                color: '#161616',
                margin: 0
            }}>
            일정
            </h1>
            {/* 오늘의 일정, 전체 일정 영역*/}
            <div style={{
                width: '100%',
                height: isMobile ? '100%' : 'auto',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                gap: '16px',
                flex: isMobile ? '1 1 0' : '0 0 auto',
                overflow: isMobile ? 'hidden' : 'visible'
            }}>
                {/* 오늘의 일정 영역*/}
                <div style={{
                    width: isMobile ? '100%' : '352px',
                    height: isMobile ? '100%' : 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    gap: '20px',
                    flex: isMobile ? '1 1 0' : '0 0 auto',
                    overflow: isMobile ? 'hidden' : 'visible'
                }}>
                    {/* 오늘의 일정 제목 영역*/}
                    <h2 style={{
                        width: '100%',
                        fontSize: isMobile ? '16px' : '18px',
                        fontWeight: '600',
                        lineHeight: isMobile ? '24px' : '27px',
                        wordWrap: 'break-word',
                        color: '#161616',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                        margin: 0
                    }}>
                        오늘의 일정
                    </h2>
                    <div style={{
                        width: '100%',
                        height: isMobile ? '100%' : '685px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                        flex: isMobile ? '1 1 0' : '0 0 auto',
                        overflow: isMobile ? 'hidden' : 'visible',
                        maxHeight: isMobile ? '100%' : 'none'
                    }}>
                        {/* 달력 카드*/}
                        <Card
                            width="100%"
                            height={isMobile ? undefined : "337px"}
                            padding={isMobile ? "16px 12px 20px 12px" : "16px 20px 20px 20px"}
                            alignItems="flex-start"
                            borderBottomRightRadius="0"
                            borderBottomLeftRadius="0"
                            gap={0}
                            style={isMobile ? {
                                flex: '0 0 auto',
                                maxHeight: '50%'
                            } : {}}
                        >
                            <div style={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                justifyContent: 'flex-start',
                                gap: '16px',
                                boxSizing: 'border-box'
                            }}>
                                {/* 달력 제목 영역*/}
                                <div style={{
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                }}>
                                <h3 style={{
                                    width: '100%',
                                    fontSize: '18px',
                                    fontWeight: '600',
                                    lineHeight: '27px',
                                    wordWrap: 'break-word',
                                    color: '#161616',
                                    margin: 0
                                }}>
                                    {currentDate.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long'})}
                                </h3>
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '4px'
                                }}>
                                    <div 
                                        style={{ 
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                        onClick={goToPreviousMonth}
                                    >
                                        <LeftArrowIcon width={20} height={20} color="#161616" />
                                    </div>
                                    <div 
                                        style={{ 
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                        onClick={goToNextMonth}
                                    >
                                        <RightArrowIcon width={20} height={20} color="#161616" />
                                    </div>
                                </div>
                                </div>

                                {/* 달력 영역*/}
                                <div style={{
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'stretch',
                                    justifyContent: 'flex-start',
                                    gap: '12px',
                                    boxSizing: 'border-box'
                                }}>
                                    {/* 달력 요일 영역*/}
                                    <div style={{ 
                                    height: '20px',
                                    width: '100%',
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(7, 1fr)',
                                    columnGap: isMobile ? '4px' : '24px',
                                    alignItems: 'center',
                                    color: 'var(--Color-text-normal, #161616)',
                                    fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                                    fontSize: 'var(--Font-Font-size-Body-4, 13px)',
                                    fontStyle: 'normal',
                                    fontWeight: '500',
                                    lineHeight: '150%',
                                    }}>
                                    <span style={{ textAlign: 'center' }}>일</span>
                                    <span style={{ textAlign: 'center' }}>월</span>
                                    <span style={{ textAlign: 'center' }}>화</span>
                                    <span style={{ textAlign: 'center' }}>수</span>
                                    <span style={{ textAlign: 'center' }}>목</span>
                                    <span style={{ textAlign: 'center' }}>금</span>
                                    <span style={{ textAlign: 'center' }}>토</span>
                                    </div>
                                    {/* 달력 그리드 */}
                                    <div style={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(7, 1fr)',
                                    columnGap: isMobile ? '4px' : '24px',
                                    rowGap: isMobile ? '4px' : '4px',
                                    padding: '0',
                                    boxSizing: 'border-box'
                                    }}>
                                     {generateCalendar().map((day, index) => (
                                         <div 
                                         key={index}
                                         onClick={() => handleDateClick(day.date, day.isCurrentMonth)}
                                         onDoubleClick={() => handleDateDoubleClick(day.date, day.isCurrentMonth)}
                                 style={{ 
                                 display: 'flex',
                                 width: '100%',
                                 height: 'auto',
                                 minHeight: isMobile ? '40px' : '34px',
                                 flexDirection: 'column',
                                     alignItems: 'center',
                                     justifyContent: 'center',
                                     gap: isMobile ? '2px' : '6px',
                                     cursor: day.isCurrentMonth ? 'pointer' : 'default'
                         }}
                         >
                        {/* 날짜 기입 영역 */}
                        <div style={{
                        display: 'flex', 
                            width: '100%',
                            height: 'auto',
                            minWidth: isMobile ? '20px' : 'auto',
                            minHeight: isMobile ? '20px' : '24px',
                            maxWidth: '100%',
                            maxHeight: '100%',
                            padding: isMobile ? '2px' : '3px',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '8px',
                            flexShrink: '1',
                            aspectRatio: '1/1',   
                             color: day.isCurrentMonth 
                             ? (day.isSelected ? 'var(--Color-text-inverse, #FFF)' : 'var(--Color-text-normal, #161616)')
                             : 'var(--Color-text-assistive, #989898)',
                             backgroundColor: day.isSelected ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'transparent',
                             borderRadius: day.isSelected ? '50%' : '0',
                            textAlign: 'center',
                            fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                            fontSize: isMobile ? 'clamp(10px, 2.5vw, 13px)' : 'var(--Font-Font-size-Body-4, 13px)',
                            fontStyle: 'normal',
                            fontWeight: '500',
                            lineHeight: '150%'
                        }}>
                            {day.date}
                        </div>
                        {/* 일정 아이콘 */}
                        <div style={{
                            display: 'flex',
                            width: '100%',
                            height: isMobile ? '8px' : '100%',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            {day.isCurrentMonth && schedules.some(schedule => {
                                const calendarYear = currentDate.getFullYear();
                                const calendarMonth = currentDate.getMonth();
                                return isScheduleOnDate(schedule, day.date, calendarYear, calendarMonth);
                            }) && (
                            <img 
                                src={'/images/icons/circleSky.svg'} 
                                alt="일정 있음"
                                style={{
                                    width: isMobile ? '6px' : 'auto',
                                    height: isMobile ? '6px' : 'auto'
                                }}
                            />
                            )}
                        </div>
                        </div>
                        ))}
                                   </div>
                                </div>
                            </div>
                        </Card>
                        {/* 오늘의 일정 카드*/}
                        <Card
                            width="100%"
                            height={isMobile ? undefined : "351px"}
                            padding="16px 20px"
                            alignItems="flex-start"
                            borderTopRightRadius="0"
                            borderTopLeftRadius="0"
                            borderTop="4px solid #F0F0F0"
                            gap={0}
                            style={isMobile ? {
                                flex: '1 1 0',
                                maxHeight: '100%',
                                overflow: 'auto'
                            } : {}}
                        >
                            <div style={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                justifyContent: 'flex-start',
                                gap: '4px'
                            }}>
                                <div style={{
                                    width: '100%',
                                    height: '27px',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'flex-start',
                                    justifyContent: 'flex-start',
                                    gap: '8px',
                                    padding: '8px 0px 8px 0px',
                                    flex: '0 0 auto'
                                }}>
                                    <img src={'/images/icons/calendar.svg'} alt="calendar" 
                                    style={{
                                        width: '24px',
                                        height: '24px',
                                        padding: '2.4px 3.4px'
                                    }}
                                    />
                                     <h3 style={{
                                         width: '100%',
                                         height: '100%',
                                         fontSize: '18px',
                                         fontWeight: '600',
                                         lineHeight: '27px',
                                         wordWrap: 'break-word',
                                         color: '#161616',
                                         margin: 0
                                     }}>
                                         {selectedDate.getDate()}일 {['일', '월', '화', '수', '목', '금', '토'][selectedDate.getDay()]}요일 일정
                                     </h3>

                                </div>
                                <div style={{
                                    width: '100%',
                                    height: isMobile ? undefined : '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    justifyContent: 'flex-start',
                                    flex: isMobile ? '1 1 0' : '0 0 auto',
                                    overflow: isMobile ? 'auto' : 'visible',
                                    minHeight: 0
                                }}>
                                     {schedules.filter(schedule => {
                                         const selectedDateValue = selectedDate.getDate();
                                         const selectedMonth = selectedDate.getMonth();
                                         const selectedYear = selectedDate.getFullYear();
                                         
                                         return isScheduleOnDate(schedule, selectedDateValue, selectedYear, selectedMonth);
                                     }).map((schedule) => (
                                        <div style={{
                                            width: '100%',
                                            height: '82px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            justifyContent: 'flex-start',
                                            padding: '16px 0px 16px 0px',
                                            gap: '8px',
                                            borderBottom: '1px solid #F0F0F0'
                                        }}>
                                            <div style={{
                                                width: '100%',
                                                height: '21px',
                                                display: 'flex',
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                                justifyContent: 'flex-start',
                                                gap: '11px'
                                            }}>
                                                <img src={'/images/icons/circleBlack.svg'} alt="원 아이콘"
                                                style={{
                                                    width: '7px',
                                                    height: '7px',
                                                }}
                                                />
                                                <span style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    fontSize: '14px',
                                                    fontWeight: '500',
                                                    lineHeight: '21px',
                                                    color: '#161616',
                                                    margin: 0
                                                }}>
                                                    {schedule.title}
                                                </span>
                                            </div>
                                            <span style={{
                                                width: '100%',
                                                height: '20px',
                                                fontSize: '13px',
                                                fontWeight: '400',
                                                lineHeight: '19.50px',
                                                color: 'var(--Color-text-normal, #161616)',
                                                wordWrap: 'break-word',
                                                margin: 0
                                            }}>
                                                {schedule.description}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>
                {/* 전체 일정 영역*/}
                {!isMobile && (
                <div style={{
                    width: '912px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    gap: '20px'
                }}>
                    <div style={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '8px'
                    }}>
                        <h2 style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        lineHeight: '27px',
                        wordWrap: 'break-word',
                        color: '#161616',
                        margin: 0
                    }}>
                        전체 일정
                    </h2>
                        <button
                            onClick={() => {
                                setSelectedDateForSchedule(new Date());
                                setDialogMode('create');
                                setEditingSchedule(null);
                                setIsAddScheduleDialogOpen(true);
                            }}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0
                            }}
                        >
                            <PlusIcon width={18} height={18} fill="#161616" />
                        </button>
                    </div>
                    <Card
                        width="100%"
                        height="685px"
                        padding="16px 20px"
                        alignItems="flex-start"
                        gap={0}
                    >
                        {/* 전체 일정 테이블 헤더*/}
                        <div style={{
                            width: '100%',
                            height: '42px',
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'flex-start',
                            justifyContent: 'flex-start',
                            padding: '5px 0px',
                            fontSize: '13px',
                            fontWeight: '500',
                            lineHeight: '150%',
                            color: 'var(--Color-text-assistive, #989898)',
                            wordWrap: 'break-word',
                            margin: 0,
                            borderBottom: '1px solid #F0F0F0'
                        }}>
                            <span style={{
                                padding: '0px 16px 12px 16px',
                                width: '67px'
                            }}>
                                No
                            </span>
                            <span style={{
                                padding: '0px 8px 12px 8px',
                                width: '265px',
                            }}>
                                일정 제목
                            </span>
                            <span style={{
                                padding: '0px 8px 12px 8px',
                                width: '268px',
                            }}>
                                기간
                            </span>
                            <span style={{
                                padding: '0px 8px 12px 8px',
                                width: '87px',
                            }}>
                                유형
                            </span>
                            <span style={{
                                padding: '0px 8px 12px 8px',
                                width: '101px',
                            }}>
                                상태
                            </span>
                            <span style={{
                                padding: '0px 8px 12px 8px',
                                width: '84px',
                            }}>
                                관리
                            </span>
                        </div>
                        {/* 전체 일정 테이블 바디*/}
                        <div style={{
                            width: '100%',
                            height: '547px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            justifyContent: 'flex-start',
                         }}>
                             {currentSchedules.map((schedule, index) => (
                                <div 
                                    key={schedule.id}
                                    style={{
                                    width: '100%',
                                    height: '56px',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                    borderBottom: '1px solid #F0F0F0',
                                    padding: '5px 0px 5px 0px',
                                    fontSize: '14px',
                                    fontWeight: '400',
                                    lineHeight: '21px',
                                    color: 'var(--Color-text-normal, #161616)',
                                    wordWrap: 'break-word',
                                    margin: 0,
                                }}>
                                    <span style={{
                                        padding: '12px 18px 12px 18px',
                                        width: '67px',
                                        height: '45px',
                                    }}>
                                        {(currentPage - 1) * itemsPerPage + index + 1}
                                    </span>
                                    <div style={{
                                        padding: '12px 8px 12px 8px',
                                        width: '265px',
                                        height: '45px',
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'flex-start',
                                        justifyContent: 'flex-start',
                                        gap: '2px',
                                    }}>
                                        <img src={'/images/icons/newIcon.svg'} alt="newIcon" 
                                        style={{
                                            width: '20px',
                                            height: '20px',
                                            padding: '2px 2px'
                                        }}
                                        />
                                        <span style={{
                                            width: '100%',
                                            height: '100%'
                                        }}>
                                            {schedule.title}
                                        </span>
                                    </div>
                                    <span style={{
                                        padding: '12px 8px 12px 8px',
                                        width: '268px',
                                        height: '45px',
                                    }}>
                                        {/* 날짜 표시 */}
                                        {schedule.dateOption === 'allDay' && schedule.date && 
                                            schedule.date.toDate().toLocaleDateString('ko-KR')
                                        }
                                        {schedule.dateOption === 'custom' && schedule.startDate && schedule.endDate && 
                                            `${schedule.startDate.toDate().toLocaleDateString('ko-KR')} ~ ${schedule.endDate.toDate().toLocaleDateString('ko-KR')}`
                                        }
                                        {schedule.dateOption === 'period' && schedule.period && 
                                            `${schedule.period}교시`
                                        }
                                    </span>
                                    <span style={{
                                        padding: '12px 8px 12px 8px',
                                        width: '87px',
                                        height: '45px',
                                    }}>
                                        {schedule.type}
                                    </span>
                                    <div style={{
                                        padding: '12px 8px 12px 8px',
                                        width: '101px',
                                        height: '45px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'flex-start'
                                    }}>
                                        <ToggleSwitch
                                            checked={schedule.isActive ?? false}
                                            onChange={(checked) => {
                                                setSchedules(prev => prev.map(s => 
                                                    s.id === schedule.id 
                                                        ? { ...s, isActive: checked }
                                                        : s
                                                ));
                                            }}
                                            size="medium"
                                            color="#FF6F0F"
                                        />
                                    </div>
                                    <div style={{
                                        padding: '12px 8px 12px 8px',
                                        width: '84px',
                                        height: '45px',
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'flex-start',
                                        justifyContent: 'flex-start',
                                        gap: '9px'
                                    }}>
                                        <div 
                                            style={{
                                                padding: '2px 2px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                            onClick={() => handleEditSchedule(schedule)}
                                        >
                                            <SettingIcon width={16} height={16} color="#161616" />
                                        </div>
                                        <div 
                                            style={{
                                                padding: '2px 2px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                            onClick={() => handleDeleteSchedule(schedule)}
                                        >
                                            <TrashIcon width={12} height={14} color="#161616" />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div style={{
                            width: '100%',
                            height: '64px',
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '20px 0px 20px 0px',
                            gap: '8px'
                        }}>
                             {/* 맨 처음으로 */}
                             <div style={{ display: 'flex', alignItems: 'center' }}>
                                 <div 
                                     style={{ 
                                         cursor: currentPage === 1 ? 'default' : 'pointer',
                                         display: 'flex',
                                         alignItems: 'center',
                                         justifyContent: 'center',
                                         width: '24px',
                                         height: '24px'
                                     }}
                                     onClick={currentPage === 1 ? undefined : goToFirstPage}
                                 >
                                     <LeftArrowDoubleIcon 
                                         width={24} 
                                         height={24} 
                                         color={currentPage === 1 ? '#989898' : '#161616'}
                                     />
                                 </div>
                                 {/* 이전 페이지 */}
                                 <div 
                                     style={{ 
                                         cursor: currentPage === 1 ? 'default' : 'pointer',
                                         display: 'flex',
                                         alignItems: 'center',
                                         justifyContent: 'center',
                                         width: '24px',
                                         height: '24px'
                                     }}
                                     onClick={currentPage === 1 ? undefined : goToPreviousPage}
                                 >
                                     <LeftArrowIcon 
                                         width={24} 
                                         height={24} 
                                         color={currentPage === 1 ? '#989898' : '#161616'}
                                     />
                                 </div>
                             </div>
                             
                             {/* 페이지 번호들 - 모든 페이지 표시 */}
                             <div style={{ display: 'flex', alignItems: 'center' }}>
                                 {[...Array(totalPages)].map((_, i) => {
                                     const pageNum = i + 1;
                                     
                                     return (
                                         <div 
                                             key={pageNum} 
                                             style={{
                                                 width: '24px',
                                                 height: '24px',
                                                 display: 'flex',
                                                 alignItems: 'center',
                                                 justifyContent: 'center',
                                                 cursor: 'pointer'
                                             }}
                                             onClick={() => goToPage(pageNum)}
                                         >
                                             <span style={{
                                                 fontSize: '14px',
                                                 fontWeight: '500',
                                                 color: currentPage === pageNum ? '#161616' : '#989898',
                                                 lineHeight: '14px'
                                             }}>
                                                 {pageNum}
                                             </span>
                                         </div>
                                     );
                                 })}
                             </div>
                             
                             {/* 다음으로 */}
                             <div style={{ display: 'flex', alignItems: 'center' }}>
                                 {/* 다음 페이지 */}
                                 <div 
                                     style={{ 
                                         cursor: currentPage === totalPages ? 'default' : 'pointer',
                                         display: 'flex',
                                         alignItems: 'center',
                                         justifyContent: 'center',
                                         width: '24px',
                                         height: '24px'
                                     }}
                                     onClick={currentPage === totalPages ? undefined : goToNextPage}
                                 >
                                     <RightArrowIcon 
                                         width={24} 
                                         height={24} 
                                         color={currentPage === totalPages ? '#989898' : '#161616'}
                                     />
                                 </div>
                                 {/* 맨 끝으로 */}
                                 <div 
                                     style={{ 
                                         cursor: currentPage === totalPages ? 'default' : 'pointer',
                                         display: 'flex',
                                         alignItems: 'center',
                                         justifyContent: 'center',
                                         width: '24px',
                                         height: '24px'
                                     }}
                                     onClick={currentPage === totalPages ? undefined : goToLastPage}
                                 >
                                     <RightArrowDoubleIcon 
                                         width={24} 
                                         height={24} 
                                         color={currentPage === totalPages ? '#989898' : '#161616'}
                                     />
                                 </div>
                             </div>
                        </div>
                    </Card>
                </div>
                )}
            </div>
        </div>

        {/* ScheduleSettingDialog */}
        <ScheduleSettingDialog
            isOpen={isAddScheduleDialogOpen}
            mode={dialogMode}
            initialData={editingSchedule}
            onClose={() => {
                setIsAddScheduleDialogOpen(false);
                setSelectedDateForSchedule(null);
                setEditingSchedule(null);
                setDialogMode('create');
            }}
            selectedDate={selectedDateForSchedule}
            onScheduleSaved={async (schedule: any) => {
                if (!currentUser || !isTeacher(currentUser)) return;
                
                const teacher = currentUser as Teacher;
                
                try {
                    // 날짜 처리
                    let dateField: Timestamp | undefined;
                    let startDateField: Timestamp | undefined;
                    let endDateField: Timestamp | undefined;
                    
                    if (schedule.dateOption === 'allDay' && schedule.startDate) {
                        // 날짜만 저장 (시간 정보 제거)
                        const date = new Date(schedule.startDate);
                        dateField = Timestamp.fromDate(new Date(date.getFullYear(), date.getMonth(), date.getDate()));
                    } else if (schedule.dateOption === 'custom') {
                        if (schedule.startDate) {
                            // 시작일: 해당 날짜의 00:00:00
                            const startDate = new Date(schedule.startDate);
                            startDateField = Timestamp.fromDate(new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()));
                        }
                        if (schedule.endDate) {
                            // 종료일: 해당 날짜의 23:59:59로 저장 (하루 종일 포함)
                            const endDate = new Date(schedule.endDate);
                            endDateField = Timestamp.fromDate(new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59));
                        }
                    } else if (schedule.dateOption === 'period' && selectedDateForSchedule) {
                        // period 옵션의 경우 선택된 날짜 사용
                        dateField = Timestamp.fromDate(selectedDateForSchedule);
                    }
                    
                    const scheduleData: Partial<Schedule> = {
                        title: schedule.title,
                        description: schedule.description,
                        category: schedule.category || '기타',
                        dateOption: schedule.dateOption,
                        isActive: schedule.isActive,
                        ...(dateField && { date: dateField }),
                        ...(startDateField && { startDate: startDateField }),
                        ...(endDateField && { endDate: endDateField }),
                        ...(schedule.period && { period: parseInt(schedule.period) }),
                        createdBy: teacher.uid,
                        createdAt: serverTimestamp(),
                        updatedAt: serverTimestamp()
                    };
                    
                    if (schedule.hasGoal && schedule.goal) {
                        scheduleData.hasGoal = true;
                        scheduleData.goal = {
                            type: schedule.goal,
                            value: parseInt(schedule.goal)
                };
                    }
                    
                    // 일정 타입 결정
                    const scheduleType = schedule.type === '교과' ? 'subject' : 'class';
                    scheduleData.type = scheduleType;
                
                    if (dialogMode === 'edit' && editingSchedule?.id) {
                        // 수정
                        let docRef;
                        if (scheduleType === 'class' && teacher.classId) {
                            docRef = doc(
                                db,
                                `schools/${teacher.schoolId}/classes/${teacher.classId}/schedules/${editingSchedule.id}`
                            );
                        } else if (scheduleType === 'subject' && teacher.subjectId) {
                            docRef = doc(
                                db,
                                `schools/${teacher.schoolId}/subjects/${teacher.subjectId}/schedules/${editingSchedule.id}`
                            );
                        }
                        
                        if (docRef) {
                            await updateDoc(docRef, {
                                ...scheduleData,
                                updatedAt: serverTimestamp()
                            });
                            setSuccess('일정이 수정되었습니다.');
                        }
                } else {
                        // 추가
                        if (scheduleType === 'class' && teacher.classId) {
                            const schedulesRef = collection(
                                db,
                                `schools/${teacher.schoolId}/classes/${teacher.classId}/schedules`
                            );
                            await addDoc(schedulesRef, scheduleData);
                            setSuccess('일정이 추가되었습니다.');
                        } else if (scheduleType === 'subject' && teacher.subjectId) {
                            const schedulesRef = collection(
                                db,
                                `schools/${teacher.schoolId}/subjects/${teacher.subjectId}/schedules`
                            );
                            await addDoc(schedulesRef, scheduleData);
                            setSuccess('일정이 추가되었습니다.');
                        } else {
                            setError('학급 또는 교과 정보가 없습니다.');
                        }
                }
                
                setIsAddScheduleDialogOpen(false);
                setSelectedDateForSchedule(null);
                setEditingSchedule(null);
                setDialogMode('create');
                } catch (err) {
                    console.error('Error saving schedule:', err);
                    setError('일정 저장 중 오류가 발생했습니다.');
                }
            }}
            onScheduleDeleted={async (scheduleId) => {
                if (!currentUser || !isTeacher(currentUser)) return;
                
                const teacher = currentUser as Teacher;
                
                try {
                    // 수정 중인 일정 정보 가져오기
                    const scheduleToDelete = editingSchedule;
                    
                    if (!scheduleToDelete) {
                        setError('삭제할 일정 정보를 찾을 수 없습니다.');
                        return;
                    }
                    
                    // 일정 타입에 따라 적절한 경로에서 삭제
                    const scheduleType = scheduleToDelete.type === 'subject' ? 'subject' : 'class';
                    
                    let docRef;
                    if (scheduleType === 'class' && teacher.classId) {
                        docRef = doc(
                            db,
                            `schools/${teacher.schoolId}/classes/${teacher.classId}/schedules/${scheduleId}`
                        );
                    } else if (scheduleType === 'subject' && teacher.subjectId) {
                        docRef = doc(
                            db,
                            `schools/${teacher.schoolId}/subjects/${teacher.subjectId}/schedules/${scheduleId}`
                        );
                    }
                    
                    if (docRef) {
                        await deleteDoc(docRef);
                        setSuccess('일정이 삭제되었습니다.');
                    } else {
                        setError('학급 또는 교과 정보가 없습니다.');
                    }
                    
                setIsAddScheduleDialogOpen(false);
                setSelectedDateForSchedule(null);
                setEditingSchedule(null);
                setDialogMode('create');
                } catch (err) {
                    console.error('Error deleting schedule:', err);
                    setError('일정 삭제 중 오류가 발생했습니다.');
                }
            }}
        />

    </div>
  );
};



export default ScheduleManagement;